import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { h } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useUserStore } from '@/store/modules/user';
import { getToken } from '@/utils/auth';
import { tansParams, blobValidate } from '@/utils/ruoyi';
import cache from '@/plugins/cache';
import { HttpStatus } from '@/enums/RespEnum';
import { errorCode } from '@/utils/errorCode';
import { LoadingInstance } from 'element-plus/es/components/loading/src/loading';
import FileSaver from 'file-saver';
import { getLanguage } from '@/lang';
import { encryptBase64, encryptWithAes, generateAesKey, decryptWithAes, decryptBase64 } from '@/utils/crypto';
import { encrypt, decrypt } from '@/utils/jsencrypt';
import { getSafeRedirectPath } from '@/utils';
import router from '@/router';

const encryptHeader = 'encrypt-key';
let downloadLoadingInstance: LoadingInstance;
// 是否显示重新登录
export const isRelogin = { show: false };
let isUnauthorizedHandling = false;

// 格式化后端追踪标识，便于用户反馈错误定位信息。
const getTraceIdValue = (traceId: unknown) => {
  if (traceId === undefined || traceId === null || traceId === '') return '';
  return String(traceId);
};

// 兼容追踪标识位于响应根节点或 data 节点的返回结构。
const getResponseTraceId = (responseData: any) => {
  return getTraceIdValue(responseData?.traceId ?? responseData?.data?.traceId);
};

// 通知中同时展示业务错误码和追踪编号，便于用户反馈和日志定位。
const getErrorNotificationMessage = (code: unknown, traceId: unknown) => {
  const traceIdValue = getTraceIdValue(traceId);
  const errorCodeLabel = `错误码：${ String(code)}`;
  return traceIdValue ? `${ errorCodeLabel}\n追踪编号：${ traceIdValue}` : errorCodeLabel;
};

// 使用 VNode 分开展示错误信息和追踪标识，避免长标识破坏通知布局。
const getTraceIdContent = (traceId: unknown) => {
  const traceIdValue = getTraceIdValue(traceId);
  return h('div', { class: 'request-trace-id' }, [
    h('span', { class: 'request-trace-id__label' }, '追踪编号：'),
    h('code', { class: 'request-trace-id__value' }, traceIdValue),
  ]);
};

const getMessageWithTraceIdContent = (message: string, traceId: unknown) => {
  const traceIdValue = getTraceIdValue(traceId);
  if (!traceIdValue) return message;

  return h('div', { class: 'request-error-content' }, [
    h('div', { class: 'request-error-content__message' }, message),
    getTraceIdContent(traceIdValue),
  ]);
};

const getUnauthorizedMessage = (reasonCode: unknown) => {
  let message = '当前登录状态已失效，请重新登录。';
  if (String(reasonCode) === '-5') {
    message = '当前账户状态发生变化，请重新登录。';
  } else if (String(reasonCode) === '-4') {
    message = '账号在其他地方登录，请重新登录。';
  }
  return message;
};

// 处理登录失效，展示原因后再引导用户重新登录。
const handleUnauthorized = (reasonCode?: unknown, traceId?: unknown) => {
  if (isUnauthorizedHandling) return Promise.resolve();
  isUnauthorizedHandling = true;

  const redirect = encodeURIComponent(getSafeRedirectPath(router.currentRoute.value.fullPath || '/'));
  useUserStore().logout();
  return ElMessageBox.alert(getMessageWithTraceIdContent(getUnauthorizedMessage(reasonCode), traceId), '需要重新登录', {
    confirmButtonText: '重新登录',
    type: 'warning',
    customClass: 'relogin-message-box',
    modalClass: 'relogin-message-box-overlay',
    showClose: false,
    closeOnClickModal: false,
    closeOnPressEscape: false,
  })
    .catch(() => undefined)
    .then(() => router.replace({ path: '/login', query: { redirect } }))
    .finally(() => {
      isRelogin.show = false;
      isUnauthorizedHandling = false;
    });
};

export const globalHeaders = () => {
  return {
    Authorization: `Bearer ${ getToken()}`,
    clientid: import.meta.env.VITE_APP_CLIENT_ID,
  };
};

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers.clientid = import.meta.env.VITE_APP_CLIENT_ID;
// 创建 axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  timeout: 50000,
});

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 对应国际化资源文件后缀
    config.headers['Content-Language'] = getLanguage();

    const isToken = config.headers?.isToken === false;
    // 是否需要防止数据重复提交
    const isRepeatSubmit = config.headers?.repeatSubmit === false;
    // 是否需要加密
    const isEncrypt = config.headers?.isEncrypt === 'true';

    if (getToken() && !isToken) {
      config.headers.Authorization = `Bearer ${ getToken()}`; // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // get请求映射params参数
    if (config.method === 'get' && config.params) {
      let url = `${config.url }?${ tansParams(config.params)}`;
      url = url.slice(0, -1);
      config.params = {};
      config.url = url;
    }

    if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
      const requestObj = {
        url: config.url,
        data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
        time: new Date().getTime(),
      };
      const sessionObj = cache.session.getJSON('sessionObj');
      if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
        cache.session.setJSON('sessionObj', requestObj);
      } else {
        const s_url = sessionObj.url; // 请求地址
        const s_data = sessionObj.data; // 请求数据
        const s_time = sessionObj.time; // 请求时间
        const interval = 500; // 间隔时间(ms)，小于此时间视为重复提交

        if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
          const message = '数据正在处理，请勿重复提交';
          console.log('------------', `${s_url}`);

          console.warn(`[${s_url}]: ${ message}`);
          return Promise.reject(new Error(message));
        } else {
          cache.session.setJSON('sessionObj', requestObj);
        }
      }
    }
    if (import.meta.env.VITE_APP_ENCRYPT === 'true') {
      // 当开启参数加密
      if (isEncrypt && (config.method === 'post' || config.method === 'put')) {
        // 生成一个 AES 密钥
        const aesKey = generateAesKey();
        config.headers[encryptHeader] = encrypt(encryptBase64(aesKey));
        config.data = typeof config.data === 'object' ? encryptWithAes(JSON.stringify(config.data), aesKey) : encryptWithAes(config.data, aesKey);
      }
    }
    // FormData数据去请求头Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
service.interceptors.response.use(
  (res: AxiosResponse) => {
    if (import.meta.env.VITE_APP_ENCRYPT === 'true') {
      // 加密后的 AES 秘钥
      const keyStr = res.headers[encryptHeader];
      // 加密
      if (keyStr != null && keyStr != '') {
        const encryptedData = res.data;
        // 请求体 AES 解密
        const base64Str = decrypt(keyStr);
        // base64 解码 得到请求头的 AES 秘钥
        const responseAesKey = decryptBase64(base64Str.toString());
        // 使用响应头密钥解码响应数据
        const decryptData = decryptWithAes(encryptedData, responseAesKey);
        // 将结果 (得到的是 JSON 字符串) 转为 JSON
        res.data = JSON.parse(decryptData);
      }
    }
    // 未设置状态码则默认成功状态
    const code = res.data.code || HttpStatus.SUCCESS;
    // 获取错误信息
    const msg = errorCode[code] || res.data.msg || errorCode.default;
    const hideErrorNotification = res.config.headers?.hideErrorNotification === true || res.config.headers?.hideErrorNotification === 'true';
    // 二进制数据则直接返回
    if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
      return res.data;
    }
    if (Number(code) === HttpStatus.UNAUTHORIZED) {
      // 登录失效处理完成后继续拒绝明确错误，避免路由守卫拿到 undefined 错误对象。
      return handleUnauthorized(res.data.data, getResponseTraceId(res.data)).then(() => Promise.reject(new Error('登录已失效')));
    } else if (code == 1100) {
      return Promise.reject(res.data);
    } else if (code === HttpStatus.SERVER_ERROR) {
      const traceIdValue = getResponseTraceId(res.data);
      if (!hideErrorNotification) {
        ElMessage({
          message: getMessageWithTraceIdContent(msg, traceIdValue),
          type: 'error',
          ...(traceIdValue ? { customClass: 'request-error-message' } : {}),
        });
      }
      return Promise.reject(new Error(msg));
    } else if (code === HttpStatus.WARN) {
      const traceIdValue = getResponseTraceId(res.data);
      if (!hideErrorNotification) {
        ElMessage({
          message: getMessageWithTraceIdContent(msg, traceIdValue),
          type: 'warning',
          ...(traceIdValue ? { customClass: 'request-warning-message' } : {}),
        });
      }
      return Promise.reject(new Error(msg));
    } else if (code !== HttpStatus.SUCCESS) {
      if (!hideErrorNotification) {
        const traceIdValue = getResponseTraceId(res.data);
        ElNotification.error({
          title: msg,
          message: getErrorNotificationMessage(code, traceIdValue),
          customClass: 'request-error-notification',
        });
      }
      return Promise.reject(new Error(msg));
    } else {
      return Promise.resolve(res.data);
    }
  },
  (error: any) => {
    if (error?.response?.status === HttpStatus.UNAUTHORIZED) {
      return handleUnauthorized(error.response.data?.data, getResponseTraceId(error.response.data)).then(() => Promise.reject());
    }
    const hideErrorNotification =
      error?.config?.headers?.hideErrorNotification === true || error?.config?.headers?.hideErrorNotification === 'true';
    let { message } = error;
    if (message == 'Network Error') {
      message = '后端接口连接异常';
    } else if (message.includes('timeout')) {
      message = '系统接口请求超时';
    } else if (message.includes('Request failed with status code')) {
      message = `系统接口${ message.substr(message.length - 3) }异常`;
    }
    if (!hideErrorNotification) {
      const traceIdValue = getResponseTraceId(error?.response?.data);
      ElMessage({
        message: getMessageWithTraceIdContent(message, traceIdValue),
        type: 'error',
        duration: 5 * 1000,
        ...(traceIdValue ? { customClass: 'request-error-message' } : {}),
      });
    }
    return Promise.reject(error);
  },
);
// 通用下载方法
export function download(url: string, params: any, fileName: string) {
  downloadLoadingInstance = ElLoading.service({ text: '正在下载数据，请稍候', background: 'rgba(0, 0, 0, 0.7)' });
  // prettier-ignore
  return service.post(url, params, {
    transformRequest: [
      (formParams: any) => {
        return tansParams(formParams);
      },
    ],
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    responseType: 'blob',
  }).then(async (resp: any) => {
    const isLogin = blobValidate(resp);
    if (isLogin) {
      const blob = new Blob([resp]);
      FileSaver.saveAs(blob, fileName);
    } else {
      const blob = new Blob([resp]);
      const resText = await blob.text();
      const rspObj = JSON.parse(resText);
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode.default;
      ElMessage.error(errMsg);
    }
    downloadLoadingInstance.close();
  }).catch((r: any) => {
    console.error(r);
    ElMessage.error('下载文件出现错误，请联系管理员！');
    downloadLoadingInstance.close();
  });
}
// 导出 axios 实例
export default service;
